import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  FolderIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Squares2X2Icon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isCollapsed: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      description: 'Overview & Analytics'
    },
    {
      name: 'Swim Lane Tracker',
      href: '/swim-lanes',
      icon: Squares2X2Icon,
      description: 'Placement Pipeline'
    },
    {
      name: 'Clients',
      href: '/clients',
      icon: UserGroupIcon,
      description: 'Client Management'
    },
    {
      name: 'Facilities',
      href: '/facilities',
      icon: BuildingOfficeIcon,
      description: 'Care Facilities'
    },
    {
      name: 'Placements',
      href: '/placements',
      icon: ClipboardDocumentListIcon,
      description: 'Placement Records'
    },
    {
      name: 'Tasks',
      href: '/tasks',
      icon: CheckCircleIcon,
      description: 'Task Management'
    },
    {
      name: 'Resources',
      href: '/resources',
      icon: FolderIcon,
      description: 'Documents & Files'
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className={`bg-white shadow-soft border-r border-gray-200 transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-72'
    } flex flex-col h-full`}>
      {/* Logo & Brand */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          {!isCollapsed && (
            <div className="ml-3">
              <h1 className="text-xl font-bold text-gray-900">Senior Placement</h1>
              <p className="text-sm text-gray-500">Management System</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.href || 
                          (item.href === '/dashboard' && location.pathname === '/');
          const Icon = item.icon;

          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`flex-shrink-0 w-5 h-5 ${
                isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'
              }`} />
              
              {!isCollapsed && (
                <div className="ml-3 flex-1">
                  <div className={`font-medium ${isActive ? 'text-white' : ''}`}>
                    {item.name}
                  </div>
                  <div className={`text-xs ${
                    isActive ? 'text-white/80' : 'text-gray-500'
                  }`}>
                    {item.description}
                  </div>
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile & Logout */}
      <div className="border-t border-gray-100 p-4">
        {user && (
          <div className="space-y-3">
            {/* User Info */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
                  <UserCircleIcon className="w-6 h-6 text-primary-600" />
                </div>
              </div>
              {!isCollapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.email}
                  </p>
                  <p className="text-xs text-primary-600 font-medium capitalize">
                    {user.role}
                  </p>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors ${
                isCollapsed ? 'justify-center' : ''
              }`}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Sign Out</span>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
